import { useProjectParams } from '@hooks/useProjectParams';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ControlledSearchableSelectField } from 'components/Form/ControlledSearchableSelectField';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogOtherCostTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import Style from 'pages/Project/BimProject/Contract/BimContractEdit/components/BimContractEditLimitedCosts.module.css';
import { useGetContractOtherCostTypes } from 'pages/Project/Common/Contract/OtherCreate/hooks/useGetContractOtherCostTypes';
import { methodologyMap } from 'types/methodology';
import { cnMixSpace, IconAlert, IconQuestion } from 'uikit';

import { useEffect, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { TooltipWrapper } from '../../TooltipWrapper';
import Styles from '../EditOtherForm.module.css';

type TOtherCostTypeSelect = {
    control: Control;
    isDisabled?: boolean | undefined;
    isTooltipHidden?: boolean | undefined;
    id?: number;
};

export const otherCostTypeSelectLabel = () => (
    <ComponentWithTooltip
        tooltipText="Расширение списка типа прочих доступно в справочнике переменных для формул прочих затрат"
        className={`${cnMixSpace({ mL: 'xs' })} ${Styles.centeredLabel}`}
    >
        <IconQuestion view="secondary" />
    </ComponentWithTooltip>
);

const getItemLabel = (item: CatalogOtherCostTypeData) => {
    const { uniqueId, name } = item;

    return (
        uniqueId === 'BimShiftMethodOrganizationCost' ? (
            <div className={Style.informerWrapper}>
                <TooltipWrapper
                    toolTipClassName={Style.informer}
                    message="Затрата может быть отнесена либо только к лимитированным, либо только к прочим"
                    className={cnMixSpace({ mR: 'xs' })}
                    style={{ pointerEvents: 'all' }}
                >
                    <IconAlert size="xs" view="warning" />
                </TooltipWrapper>
                <div>{name}</div>
            </div>
        ) : (
            name || ''
        )
    ) as string;
};

export const OtherCostTypeSelect = (props: TOtherCostTypeSelect) => {
    const { isTooltipHidden, control, isDisabled, id } = props;

    const { id: projectId, methodology } = useProjectParams();

    const { projectData } = useProjectData(projectId, false);

    const { items, status, onNextPageRequest, requestSearchByName, hasNextPage } = useGetContractOtherCostTypes({
        contractId: projectData?.contractId,
        calculationId: projectData?.calculationId,
        otherId: id,
        methodology:
            methodologyMap[ProjectMethodologyEnum.Bim] === methodology
                ? ProjectMethodologyEnum.Bim
                : ProjectMethodologyEnum.Uer,
        withEffect: false,
    });

    const {
        clearErrors,
        getValues,
        formState: { errors },
    } = useFormContext();

    const selectItems = useMemo(() => items?.map((item) => ({ label: item.name, ...item })), [items]);

    const currentOtherCostValue = getValues('otherCostType');

    useEffect(() => {
        if (errors?.otherCostType?.type === 'required' && currentOtherCostValue) {
            clearErrors('otherCostType');
        }
    }, [clearErrors, currentOtherCostValue, errors?.otherCostType?.type]);

    return (
        <ControlledSearchableSelectField
            name="otherCostType"
            label="Тип прочих"
            control={control}
            items={selectItems}
            labelIcon={otherCostTypeSelectLabel as any}
            className="labelWithEndingIcon"
            disabled={isDisabled}
            rules={{ required: 'Обязательное поле' }}
            labelPosition="left"
            isTooltipHidden={isTooltipHidden}
            direction="rightCenter"
            getItemLabel={getItemLabel}
            onSearch={requestSearchByName}
            onNextPageRequest={onNextPageRequest}
            isLoading={status === RequestStatus.FETCHING}
            hasNextPage={hasNextPage}
            isLazyLoadEnabled
        />
    );
};

OtherCostTypeSelect.displayName = 'OtherCostTypeSelect';
